import React, { useState } from "react";
import Input from "../ui/Input";
import Button from "../ui/Button";
import FormError from "../ui/FormError";
import FormGroup from "../ui/FormGroup";
import { validateEmail } from "../../utils/validation";
import { applyPhoneMask, isValidMaskedPhone, normalizePhoneForSubmit } from "../../utils/phoneMask";

interface IdentifierViewProps {
	onContinue?: (identifier: string, type: "email" | "phone" | "unknown") => void;
	isCheckingIdentifier?: boolean;
}

type IdentifierType = "email" | "phone" | "unknown";

const EMAIL_TRIGGER_REGEX = /[@a-zA-Zа-яА-ЯёЁ]/;

const isPotentialEmail = (value: string): boolean => {
	return EMAIL_TRIGGER_REGEX.test(value);
};

const getPhoneDigits = (value: string): string => {
	return value.replace(/\D/g, "");
};

const sanitizePhoneRaw = (value: string): string => {
	if (!value) return "";

	const cleaned = value.replace(/[^\d+]/g, "");

	if (cleaned.startsWith("+")) {
		return "+" + cleaned.slice(1).replace(/\+/g, "");
	}

	return cleaned.replace(/\+/g, "");
};

const getPhoneMeaningfulLength = (value: string): number => {
	if (!value) return 0;

	if (value.startsWith("+")) {
		return getPhoneDigits(value).length + 1;
	}

	return getPhoneDigits(value).length;
};

const shouldApplyPhoneMask = (rawPhone: string): boolean => {
	return getPhoneMeaningfulLength(rawPhone) >= 3;
};

const detectIdentifierType = (rawValue: string): IdentifierType => {
	if (!rawValue.trim()) {
		return "unknown";
	}

	if (isPotentialEmail(rawValue)) {
		return "email";
	}

	if (rawValue.startsWith("+")) {
		return "phone";
	}

	if (getPhoneDigits(rawValue).length >= 3) {
		return "phone";
	}

	return "unknown";
};

const applyPhoneMaskFromRaw = (rawPhone: string): string => {
	if (!rawPhone) return "";

	if (!shouldApplyPhoneMask(rawPhone)) {
		return rawPhone;
	}

	const digits = getPhoneDigits(rawPhone);

	if (!digits) {
		return rawPhone.startsWith("+") ? "+" : "";
	}

	return applyPhoneMask(rawPhone.startsWith("+") ? `+${digits}` : digits);
};

const findInsertion = (prev: string, next: string) => {
	let start = 0;

	while (start < prev.length && start < next.length && prev[start] === next[start]) {
		start++;
	}

	let prevEnd = prev.length - 1;
	let nextEnd = next.length - 1;

	while (prevEnd >= start && nextEnd >= start && prev[prevEnd] === next[nextEnd]) {
		prevEnd--;
		nextEnd--;
	}

	return {
		start,
		removed: prev.slice(start, prevEnd + 1),
		added: next.slice(start, nextEnd + 1),
	};
};

const getPhoneRawLengthFromDisplayPart = (displayPart: string): number => {
	const hasLeadingPlus = displayPart.includes("+");
	const digitsCount = getPhoneDigits(displayPart).length;

	return digitsCount + (hasLeadingPlus ? 1 : 0);
};

const sanitizeInsertedPhoneFragment = (value: string, insertIndex: number): string => {
	if (!value) return "";

	let cleaned = value.replace(/[^\d+]/g, "");

	if (!cleaned) return "";

	if (insertIndex === 0) {
		if (cleaned.startsWith("+")) {
			return "+" + cleaned.slice(1).replace(/\+/g, "");
		}

		return cleaned.replace(/\+/g, "");
	}

	return cleaned.replace(/\+/g, "");
};

const applyChangeToRaw = (prevRaw: string, prevDisplay: string, nextDisplayFromInput: string, prevType: IdentifierType): string => {
	const diff = findInsertion(prevDisplay, nextDisplayFromInput);

	if (prevType === "email") {
		return prevRaw.slice(0, diff.start) + diff.added + prevRaw.slice(diff.start + diff.removed.length);
	}

	if (isPotentialEmail(diff.added) || isPotentialEmail(nextDisplayFromInput)) {
		const leftDisplayPart = prevDisplay.slice(0, diff.start);
		const rawInsertIndex = getPhoneRawLengthFromDisplayPart(leftDisplayPart);
		const removedPhoneRaw = sanitizePhoneRaw(diff.removed);

		return prevRaw.slice(0, rawInsertIndex) + diff.added + prevRaw.slice(rawInsertIndex + removedPhoneRaw.length);
	}

	const leftDisplayPart = prevDisplay.slice(0, diff.start);
	const rawChangeIndex = getPhoneRawLengthFromDisplayPart(leftDisplayPart);

	const removedPhoneRaw = sanitizePhoneRaw(diff.removed);
	const addedPhoneRaw = sanitizeInsertedPhoneFragment(diff.added, rawChangeIndex);

	const nextRaw = prevRaw.slice(0, rawChangeIndex) + addedPhoneRaw + prevRaw.slice(rawChangeIndex + removedPhoneRaw.length);

	return sanitizePhoneRaw(nextRaw);
};

const buildDisplayIdentifier = (rawIdentifier: string, type: IdentifierType): string => {
	if (!rawIdentifier) return "";

	if (type === "email") {
		return rawIdentifier;
	}

	if (type === "phone") {
		return applyPhoneMaskFromRaw(rawIdentifier);
	}

	return rawIdentifier;
};

const IdentifierView: React.FC<IdentifierViewProps> = ({ onContinue, isCheckingIdentifier = false }) => {
	const [rawIdentifier, setRawIdentifier] = useState("");
	const [displayIdentifier, setDisplayIdentifier] = useState("");
	const [identifierType, setIdentifierType] = useState<IdentifierType>("unknown");
	const [showError, setShowError] = useState(false);
	const [errorText, setErrorText] = useState("");

	const showFloatingLabel = displayIdentifier.trim().length > 0 || showError;

	const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextDisplayFromInput = e.target.value;

		setShowError(false);
		setErrorText("");

		const nextRaw = applyChangeToRaw(rawIdentifier, displayIdentifier, nextDisplayFromInput, identifierType);

		const nextType = detectIdentifierType(nextRaw);
		const nextDisplay = buildDisplayIdentifier(nextRaw, nextType);

		setRawIdentifier(nextRaw);
		setIdentifierType(nextType);
		setDisplayIdentifier(nextDisplay);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		const trimmedRaw = rawIdentifier.trim();
		const trimmedDisplay = displayIdentifier.trim();

		if (!trimmedRaw && !trimmedDisplay) {
			setShowError(true);
			setErrorText("Введите телефон или email");
			return;
		}

		if (identifierType === "phone") {
			if (!isValidMaskedPhone(trimmedDisplay)) {
				setShowError(true);
				setErrorText("Введите телефон полностью");
				return;
			}

			onContinue?.(normalizePhoneForSubmit(trimmedDisplay), "phone");
			return;
		}

		const emailCandidate = trimmedRaw;
		const emailValidation = validateEmail(emailCandidate);

		if (!emailValidation.valid) {
			setShowError(true);
			setErrorText("Введите корректный телефон или email");
			return;
		}

		onContinue?.(emailCandidate.toLowerCase(), "email");
	};

	const handleClear = () => {
		setRawIdentifier("");
		setDisplayIdentifier("");
		setIdentifierType("unknown");
		setShowError(false);
		setErrorText("");
	};

	return (
		<div className="auth-view">
			<p className="auth-text">Отслеживайте заказы, получайте персональные предложения и участвуйте в системе лояльности</p>

			<form onSubmit={handleSubmit}>
				<FormGroup>
					<div className={`auth-field ${showError ? "is-error" : ""}`}>
						{showFloatingLabel && (
							<label htmlFor="identifier-input" className="auth-field__label">
								Телефон или email
							</label>
						)}

						<Input id="identifier-input" type="text" value={displayIdentifier} onChange={handleIdentifierChange} placeholder="Телефон или email" autoFocus disabled={isCheckingIdentifier} error={showError} clearable onClear={handleClear} />
					</div>

					{showError && <FormError message={errorText} />}
				</FormGroup>

				<Button type="submit" className="tw-w-full" loading={isCheckingIdentifier}>
					{isCheckingIdentifier ? "Проверка..." : "Продолжить"}
				</Button>

				<div className="tw-mt-4 tw-text-center">
					<span className="auth-politics-text tw-text-xs tw-text-text-secondary">
						Нажимая «Продолжить», вы соглашаетесь на{" "}
						<a
							href="https://carolinashop.ru/include/personal_data_consent.php"
							className="auth-politics-link"
							target="_blank"
							rel="noopener noreferrer"
						>
							обработку персональных данных
						</a>{" "}
						и с{" "}
						<a
							href="https://carolinashop.ru/include/public_offer.php"
							className="auth-politics-link"
							target="_blank"
							rel="noopener noreferrer"
						>
							публичной офертой
						</a>
					</span>
				</div>
			</form>
		</div>
	);
};

export default IdentifierView;
